import { useAuthContextV2 } from "../../../../auth-state";
import { PortalTemplate } from "../../../template/portal/default-template";
import { TtzkPortalR } from "../route";
import React, { useEffect } from "react";
import {
  FormContainer,
  FormContainerItem,
  RadioButton,
  Spinner,
  PrimaryButton,
  WarningBanner,
  StatusTag,
} from "@graffer-inc/console-design-kit";
import { toast } from "react-toastify";
import apiClient from "../../../../application/apis/api-client";
import {
  isNotFoundError,
  reportProcManagementRelatedError,
} from "../../../../application/domain/proc-management/util/error";
import dayjs from "dayjs";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  PortalConfig,
  PortalConfigRequest,
} from "../../../../application/domain/ttzk-portal";

// DatePickerのカスタムスタイル
const datePickerStyles = `
  .react-datepicker {
    font-size: 1.3em;
  }

  .react-datepicker__header {
    padding-top: 0.8em;
  }

  .react-datepicker__month {
    margin: 0.4em 1em;
  }

  .react-datepicker__day-name, .react-datepicker__day {
    width: 1.9em;
    line-height: 1.9em;
    margin: 0.166em;
  }

  .react-datepicker__current-month {
    font-size: 1em;
  }

  .react-datepicker__navigation {
    top: 1em;
    line-height: 1.7em;
    border: 0.45em solid transparent;
  }

  .react-datepicker__navigation--previous {
    border-right-color: #ccc;
    left: 1em;
  }

  .react-datepicker__navigation--next {
    border-left-color: #ccc;
    right: 1em;
  }

  .react-datepicker__input-container input {
    padding: 0.5em;
    font-size: 1.1em;
    width: 200px;
  }
`;

// スタイルを適用するためのstyleタグを追加するコンポーネント
const DatePickerStyles = () => <style>{datePickerStyles}</style>;

const getPublishStatusLabel = (status: string, publishDate: string | null) => {
  if (status === "unpublished") {
    return {
      text: "非公開",
      type: "SECONDARY" as const,
    };
  }

  if (!publishDate) {
    return {
      text: "公開",
      type: "SUCCESS" as const,
    };
  }

  const now = dayjs();
  const publishAt = dayjs(publishDate);

  if (publishAt.isBefore(now)) {
    return {
      text: "公開",
      type: "SUCCESS" as const,
    };
  }

  return {
    text: "公開予約",
    type: "WARN" as const,
  };
};

export const PortalConfigEdit = () => {
  const { auth } = useAuthContextV2();
  const orgId = auth.data?.orgId;
  const orgSlug = auth.data?.orgSlug;

  const [serverConfig, setServerConfig] = React.useState<PortalConfig | null>(
    null,
  );
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isSaving, setIsSaving] = React.useState<boolean>(false);
  const [publishStatus, setPublishStatus] = React.useState<
    "published" | "unpublished"
  >("unpublished");
  const [publishDate, setPublishDate] = React.useState<Date | null>(null);
  const [publishDateError, setPublishDateError] = React.useState<string>("");

  const validatePublishDate = (date: Date | null): boolean => {
    if (!date) return true;
    const now = new Date();
    return date >= now;
  };

  const isConfigChanged =
    serverConfig?.publishConfig?.status !== publishStatus ||
    (serverConfig?.publishConfig?.publishDate
      ? dayjs(serverConfig.publishConfig.publishDate).format(
          "YYYY-MM-DD HH:mm:ss",
        )
      : null) !==
      (publishDate ? dayjs(publishDate).format("YYYY-MM-DD HH:mm:ss") : null);

  const isUpdateDisabled = !!(
    publishStatus === "published" &&
    publishDate &&
    !validatePublishDate(publishDate)
  );

  const fetchConfigByOrgId = async (orgId: string) => {
    setIsLoading(true);
    try {
      const res = await apiClient.ttzkPortal.findPortalConfig(orgId);
      const config = res.data as PortalConfig;
      setServerConfig(config);
      setPublishStatus(
        config?.publishConfig?.status as "published" | "unpublished",
      );
      setPublishDate(
        config?.publishConfig?.publishDate
          ? new Date(config.publishConfig.publishDate)
          : null,
      );
    } catch (error) {
      if (!isNotFoundError(error)) {
        reportProcManagementRelatedError(error);
        const message = error.response?.data?.msg ?? error.message;
        toast.error(
          `設定の取得に失敗しました。 organizationId: ${orgId}, error message: ${message}`,
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (orgId) {
        const configToUpdate: PortalConfigRequest = {
          publishConfig: {
            status: publishStatus,
            ...(publishStatus === "published" && publishDate
              ? { publishDate: publishDate.toISOString() }
              : {}),
          },
        };
        const { data } = await apiClient.ttzkPortal.upsertPortalConfig(
          configToUpdate,
          orgId,
        );
        setServerConfig(data as PortalConfig);
        toast.success("設定を更新しました。");
      }
    } catch (error) {
      reportProcManagementRelatedError(error);
      const message = error.response?.data?.msg ?? error.message;
      toast.error(
        `設定の更新に失敗しました。 organizationId: ${orgId}, error message: ${message}`,
      );
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (orgId) {
      fetchConfigByOrgId(orgId);
    }
  }, [orgId]);

  return (
    <PortalTemplate
      breadcrumbs={[
        { to: TtzkPortalR.Home.link({}), title: "Graffer ポータル" },
        {
          to: TtzkPortalR.ConfigEdit.link({}),
          title: "公開設定",
        },
      ]}
    >
      <DatePickerStyles />
      {typeof orgId === "string" && typeof orgSlug === "string" && (
        <div className={"content"}>
          <h1>公開設定</h1>
          <hr />
          {serverConfig && (
            <div className="notification is-info is-light">
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <strong>現在のポータルの公開状態：</strong>
                <StatusTag
                  type={
                    getPublishStatusLabel(
                      serverConfig.publishConfig.status,
                      serverConfig.publishConfig.publishDate || null,
                    ).type
                  }
                  hiddenCheckIcon
                >
                  {
                    getPublishStatusLabel(
                      serverConfig.publishConfig.status,
                      serverConfig.publishConfig.publishDate || null,
                    ).text
                  }
                </StatusTag>
                {serverConfig.publishConfig.publishDate && (
                  <span>
                    （公開日時:{" "}
                    {dayjs(serverConfig.publishConfig.publishDate).format(
                      "YYYY/MM/DD HH:mm",
                    )}
                  </span>
                )}
              </div>
            </div>
          )}
          <FormContainer>
            <div className="notification is-warning is-light mb-5">
              <div className="content">
                <h4 className="has-text-weight-bold mb-2">
                  ポータル公開設定に関する重要な注意事項
                </h4>
                <ul>
                  <li>
                    手続きガイドの「地域」が公開されている場合、この設定に関わらず
                    <strong>必ずポータルは公開</strong>されます。
                  </li>
                  <li>
                    手続きガイドの「地域」が下書きの場合のみ、この設定が有効になります。
                  </li>
                  <li>
                    設定がない場合は<strong>デフォルトで公開</strong>
                    として扱われます。
                  </li>
                </ul>
                <h5 className="has-text-weight-bold mb-2 mt-4">
                  非公開設定時の影響
                </h5>
                <ul>
                  <li>
                    スマート申請の各手続きのページなど、各種ページにある左上のロゴからのポータルへのリンクが404エラーとなります。
                  </li>
                  <li>
                    手続きやアンケートを公開している場合は、UX上、ポータルは公開することを
                    <strong>強く推奨</strong>します。
                  </li>
                  <li>
                    非公開設定は、どうしても必要な場合のみ使用してください。
                  </li>
                </ul>
              </div>
            </div>

            {isConfigChanged && (
              <FormContainerItem>
                <WarningBanner message="設定が変更されています。変更を反映するには保存ボタンを押してください。" />
              </FormContainerItem>
            )}
            <FormContainerItem>
              <RadioButton
                description="ポータルの公開状態を設定します"
                label="ポータルの公開設定"
                onChange={(e) => {
                  const value = e.target.value as "published" | "unpublished";
                  setPublishStatus(value);
                  if (value === "unpublished") {
                    setPublishDate(null);
                    setPublishDateError("");
                  }
                }}
                options={[
                  {
                    name: "非公開",
                    value: "unpublished",
                  },
                  {
                    name: "公開",
                    value: "published",
                  },
                ]}
                value={publishStatus}
              />
            </FormContainerItem>
            {publishStatus === "published" && (
              <FormContainerItem>
                <div className="field">
                  <label className="label">公開日時</label>
                  <div className="control">
                    <ReactDatePicker
                      selected={publishDate}
                      onChange={(date: Date | null) => {
                        setPublishDate(date);
                        if (date && !validatePublishDate(date)) {
                          setPublishDateError("過去の日時は指定できません");
                        } else {
                          setPublishDateError("");
                        }
                      }}
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={15}
                      dateFormat="yyyy/MM/dd HH:mm"
                      placeholderText="公開日時を選択"
                      minDate={new Date()}
                    />
                  </div>
                  {publishDateError && (
                    <p className="help is-danger">{publishDateError}</p>
                  )}
                  <p className="help">
                    公開日時を指定しない場合は即時公開されます。
                    未来の日時を指定すると、その日時に公開されます。
                  </p>
                </div>
              </FormContainerItem>
            )}
            <FormContainerItem>
              <PrimaryButton
                onClick={handleSave}
                isLoading={isSaving}
                disabled={isSaving || !isConfigChanged || isUpdateDisabled}
                text="保存"
              />
            </FormContainerItem>
          </FormContainer>
          <Spinner loading={isLoading} />
        </div>
      )}
    </PortalTemplate>
  );
};
